// Lib
import React, { ReactElement, useEffect } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { compose } from '../../../node_module_clones/recompose';
import { Capacitor } from '@capacitor/core';

// Selectors
import { mobileQuickNotesPageSelector } from '../header/workspaceToolsHeader/quickNotes/quickNotesSelector';
import { quickNotesElementSelector } from '../header/workspaceToolsHeader/quickNotes/quickNotesElementsSelector';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';

// Components
import QuickNotesScrollableList from './QuickNotesScrollableList';
import PoiBoardSectionContext from '../../components/pointsOfInterest/PoiBoardSectionContext';
import LoadingSpinner from '../../components/loaders/Spinner';
import MobileQuickAddSheet from './MobileQuickAddSheet';
import useScaledMobileScrollableList from '../../canvas/zoom/list/useScaledMobileScrollableList';
import { ZoomStateContextProvider } from '../../canvas/zoom/ZoomStateContext';
import useLockScreenOrientationPortrait from '../../platform/screenOrientation/useLockScreenOrientationPortrait';

// Decorators
import elementShortcutsDecorator from '../shortcuts/clipboard/elementShortcutsDecorator';

// Plugins
import { NavigationPlugin } from '../../../capacitor_plugins/navigation';

// Constants
import { BoardSections } from '../../../common/boards/boardConstants';
import { GeneralWorkspaceProps } from '../workspaceTypes';
import { CAPACITOR_NAVIGATION_PLUGIN } from '../../../capacitor_plugins/pluginConstants';

// Styles
import './MobileQuickNotesPage.scss';

const quickNotesElementSelectorConnector = connect(quickNotesElementSelector);
const quickNotesPageSelectorConnector = connect(mobileQuickNotesPageSelector);

type Props = GeneralWorkspaceProps &
    ConnectedProps<typeof quickNotesElementSelectorConnector> &
    ConnectedProps<typeof quickNotesPageSelectorConnector> & {
        createShortcutCard: (element: object) => void;
    };

const enhance = compose<Props, GeneralWorkspaceProps>(
    quickNotesElementSelectorConnector,
    quickNotesPageSelectorConnector,
    elementShortcutsDecorator,
);

const MobileQuickNotesPage = (props: Props): ReactElement => {
    useEffect(() => {
        if (Capacitor.isPluginAvailable(CAPACITOR_NAVIGATION_PLUGIN)) {
            NavigationPlugin.onQuicknotesNavigation();
        }
    }, []);

    const {
        quickNotesRootId,
        quickNotesChildIds,
        isCurrentBoardFetching,
        currentBoard,
        gridSize,
        permissions,
        createShortcutCard,
        isCurrentBoardFetched,
    } = props;

    const platformDetails = useSelector(getPlatformDetailsSelector);

    const { listZoomScale } = useScaledMobileScrollableList(platformDetails, gridSize);

    useLockScreenOrientationPortrait(true);

    if (isCurrentBoardFetching && !isCurrentBoardFetched) {
        return (
            <div className="MobileQuickNotesPage">
                <LoadingSpinner show />
            </div>
        );
    }

    return (
        <>
            <div className="MobileQuickNotesPage">
                <PoiBoardSectionContext.Provider value={BoardSections.QUICK_NOTES}>
                    <ZoomStateContextProvider zoomScale={listZoomScale}>
                        <div className="quick-notes-scrollable-list-container">
                            <QuickNotesScrollableList
                                gridSize={gridSize}
                                permissions={permissions}
                                quickNotesRootId={quickNotesRootId}
                                quickNotesChildIds={quickNotesChildIds}
                                listZoomScale={listZoomScale}
                                createShortcutCard={createShortcutCard}
                            />
                        </div>
                    </ZoomStateContextProvider>
                </PoiBoardSectionContext.Provider>
            </div>
            <MobileQuickAddSheet currentBoard={currentBoard} />
        </>
    );
};

export default enhance(MobileQuickNotesPage);
